<!-- One /news item (an email or an old blog post) that expands in place. Collapsed: title, date
     and a one-line preview. Expanded: title, date and the full text (fetched on first expand).
     A chevron shows the state; the date, far right, opens the item's own page. -->

<template>
  <article class="news-row news-region" :class="{isExpanded}">
    <!-- left gutter: chevron at the top; while expanded, the rest of the gutter (no visible line:
         the black panel's left edge is the boundary) also collapses the item, since emails can
         run many screens tall -->
    <div class="gutter">
      <button
        class="chevron"
        type="button"
        :aria-expanded="isExpanded"
        :aria-label="isExpanded ? 'Collapse' : 'Expand'"
        @click="$emit('toggle')">
        <svg
          viewBox="0 0 24 24"
          width="14"
          height="14"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true">
          <path d="m9 6 6 6-6 6" />
        </svg>
      </button>
      <button
        v-if="isExpanded"
        class="rail"
        type="button"
        tabindex="-1"
        aria-label="Collapse"
        @click="$emit('toggle')" />
    </div>
    <div class="row-body">
      <!-- title on the left, date on the far right (a link to the item's own page). The
           heading toggles; so does the whole row while collapsed -->
      <div class="row-head">
        <h4 @click="$emit('toggle')">{{ item.title }}</h4>
        <router-link class="date" :to="itemPath" title="Open this item's own page">
          {{ getNewsDay(item.date) }}
        </router-link>
      </div>
      <!-- the pre-header as a lede, the same expanded or collapsed (one line when collapsed);
           expanded, old blog posts skip it: their body starts with that same paragraph -->
      <p
        v-if="item.excerpt && (!isExpanded || item.source !== 'blog')"
        class="lede"
        :class="{isOneLine: !isExpanded}"
        @click="!isExpanded && $emit('toggle')">
        {{ item.excerpt }}
      </p>
      <template v-if="isExpanded">
        <p v-if="!message" class="loading">Loading…</p>
        <!-- eslint-disable-next-line vue/no-v-html -- trusted: VIZBI's own emails and posts -->
        <div
          v-else-if="message.html"
          class="message"
          @click="followInternalLink($event, router)"
          v-html="message.html" />
        <p v-else-if="message.url">
          <a :href="message.url">Read the original post</a>
        </p>
      </template>
    </div>
  </article>
</template>

<script setup>
import {ref, watch} from "vue";
import {useRouter} from "vue-router";
import {fetchNewsItem, followInternalLink, getNewsDay} from "@/composables/useNews";

const props = defineProps({
  item: {type: Object, required: true}, // {slug, date, title, excerpt, source}
  itemPath: {type: String, required: true}, // its own page: /news/2026/04-30
  isExpanded: {type: Boolean, default: false}
});
defineEmits(["toggle"]);

const router = useRouter();
const message = ref(null);

watch(() => props.isExpanded, async (isExpanded) => {
  if (isExpanded && !message.value) message.value = await fetchNewsItem(props.item.slug);
}, {immediate: true});
</script>

<style scoped>
/* one news item, a black region (News/message.css): chevron gutter, then the text, at most the
   emails' 600px, with title, lede, date and body on one left edge */
.news-row {
  display: flex;
  align-items: flex-start;
  gap: 0.4rem; /* small: chevron to text */
  padding: 0.8rem 0.7rem 0.8rem 0.4rem;
}
.gutter {
  flex: none;
  align-self: stretch;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.rail {
  appearance: none;
  -webkit-appearance: none;
  flex: 1;
  width: 22px;
  margin: 4px 0 0;
  padding: 0;
  border: none;
  border-radius: 0;
  box-shadow: none;
  background: none; /* invisible: a click area only */
  cursor: pointer;
}
.chevron {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  margin-top: 0.1rem;
  color: var(--text-color-less-subtle);
}
.chevron {
  appearance: none;
  -webkit-appearance: none;
  padding: 0;
  border: none;
  border-radius: 0;
  box-shadow: none;
  background: none;
  cursor: pointer;
}
.chevron svg {
  transition: transform 0.15s ease-out;
}
.isExpanded .chevron svg {
  transform: rotate(90deg); /* right → down */
}
@media (prefers-reduced-motion: reduce) {
  .chevron svg {
    transition: none;
  }
}
.chevron:hover {
  color: var(--text-color);
}
.row-body {
  flex: 1;
  min-width: 0;
  max-width: var(--news-text-width); /* the date sits on this right edge */
}
.row-head {
  display: flex;
  align-items: baseline;
  gap: 1rem;
}
/* bold; clicking toggles the row; green while expanded */
h4 {
  flex: 1;
  min-width: 0;
  margin: 0 0 0.2rem;
  font-weight: 700;
  cursor: pointer;
}
.isExpanded h4 {
  color: var(--brand-color);
}
/* "30 April", the link to the item's page: styled as the year links (Archive.vue), smaller */
.date {
  flex: none;
  color: var(--text-color);
  font-size: 0.85em;
  white-space: nowrap;
  text-decoration: none;
}
.date:hover {
  color: var(--text-color);
  text-decoration: underline;
}
/* the pre-header as a lede (as on the item page, views/News/Item.vue) */
.lede {
  margin: 0.2rem 0 0.8rem;
  /* italic and a darker grey set it apart from the title and the body */
  color: color-mix(in srgb, var(--text-color-less-subtle) 70%, var(--text-color-subtle));
  font-size: 1.1em;
  font-style: italic;
  line-height: 1.4;
}
/* collapsed: one line, cut with an ellipsis */
.lede.isOneLine {
  margin-bottom: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  cursor: pointer;
}
.loading {
  margin: 0;
}
</style>
